import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useConfirm } from '../../../components/ConfirmDialog';
import { formatDate } from '../../../utils/formatDateTime';
import { getBlogPosts, deleteBlogPost } from '../../../services/api';

/**
 * Listagem do blog institucional. Master pode criar, editar e remover posts.
 *
 * Status possíveis:
 *   - draft      → rascunho (não aparece em /blog público)
 *   - published  → publicado (aparece em /blog se published_at <= agora)
 *
 * Dica: status=published com published_at futuro = post agendado.
 */

const STATUS_META = {
  draft:     { label: 'Rascunho',  color: '#f59e0b', bg: '#fef3c7' },
  published: { label: 'Publicado', color: '#16a34a', bg: '#dcfce7' },
};

export default function MasterBlog() {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [posts,   setPosts]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [search,  setSearch]  = useState('');
  const [statusF, setStatusF] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    getBlogPosts({ search: search || undefined, status: statusF || undefined })
      .then(r => setPosts(r.data.posts || []))
      .catch(() => toast.error('Erro ao carregar posts'))
      .finally(() => setLoading(false));
  }, [search, statusF]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (post) => {
    const ok = await confirm({
      title: `Remover post "${post.title}"?`,
      message: 'Esta ação é permanente. O post some do blog e das buscas do Google em alguns dias.',
      confirmText: 'Remover',
      variant: 'danger',
    });
    if (!ok) return;
    try {
      await deleteBlogPost(post.id);
      toast.success('Post removido.');
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao remover');
    }
  };

  const counts = posts.reduce((acc, p) => { acc[p.status] = (acc[p.status] || 0) + 1; return acc; }, {});

  return (
    <div className="page-body">
      <div className="page-header-row">
        <div className="page-header">
          <h2>Blog</h2>
          <p>Artigos institucionais publicados em atendoapp.com/blog</p>
        </div>
        <button className="btn btn-primary" onClick={() => navigate('/master/blog/new')}>
          + Novo post
        </button>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <button
          onClick={() => setStatusF('')}
          style={{
            padding: '8px 14px',
            border: '1.5px solid ' + (statusF === '' ? 'var(--primary)' : 'var(--gray-200)'),
            background: statusF === '' ? 'var(--primary)' : 'transparent',
            color: statusF === '' ? 'white' : 'var(--gray-700)',
            borderRadius: 8,
            cursor: 'pointer',
            fontSize: 13,
            fontWeight: 600,
          }}>
          Todos <span style={{ opacity: 0.7, marginLeft: 4 }}>({posts.length})</span>
        </button>
        {Object.keys(STATUS_META).map(s => {
          const meta = STATUS_META[s];
          const active = statusF === s;
          return (
            <button
              key={s}
              onClick={() => setStatusF(s)}
              style={{
                padding: '8px 14px',
                border: '1.5px solid ' + (active ? meta.color : 'var(--gray-200)'),
                background: active ? meta.color : 'transparent',
                color: active ? 'white' : meta.color,
                borderRadius: 8,
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: 600,
              }}>
              {meta.label} <span style={{ opacity: 0.7, marginLeft: 4 }}>({counts[s] || 0})</span>
            </button>
          );
        })}
      </div>

      <div className="filter-bar">
        <input
          className="search-input"
          placeholder="Buscar por título ou slug..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      {loading ? (
        <div className="spinner" style={{ margin: '40px auto', display: 'block' }} />
      ) : posts.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">✍️</div>
          <h3>{search ? 'Nenhum post encontrado' : 'Ainda nenhum post'}</h3>
          <p>{search ? 'Tente outro termo.' : 'Crie o primeiro artigo — Google adora conteúdo novo.'}</p>
          {!search && (
            <button className="btn btn-primary" style={{ marginTop: 12 }} onClick={() => navigate('/master/blog/new')}>
              Criar primeiro post
            </button>
          )}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {posts.map(p => {
            const meta = STATUS_META[p.status] || STATUS_META.draft;
            const tags = (p.tags || '').split(',').filter(Boolean);
            return (
              <div
                key={p.id}
                className="mobile-card"
                style={{
                  background: 'var(--white)',
                  borderRadius: 10,
                  border: '1px solid var(--gray-200)',
                  borderLeft: `4px solid ${meta.color}`,
                  padding: 14,
                  display: 'flex',
                  gap: 14,
                  cursor: 'pointer',
                  alignItems: 'flex-start',
                }}
                onClick={() => navigate(`/master/blog/${p.id}/edit`)}
              >
                {p.cover_image_url ? (
                  <img
                    src={p.cover_image_url} alt=""
                    style={{ width: 88, height: 66, objectFit: 'cover', borderRadius: 6, flexShrink: 0 }}
                    onError={e => { e.target.style.display = 'none'; }}
                  />
                ) : (
                  <div style={{
                    width: 88, height: 66, borderRadius: 6, flexShrink: 0,
                    background: 'linear-gradient(135deg,var(--primary),var(--primary-dark))',
                    color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 28, fontWeight: 800,
                  }}>
                    {p.title?.[0]?.toUpperCase() || '✍'}
                  </div>
                )}

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 4 }}>
                    <span style={{
                      background: meta.bg, color: meta.color, fontWeight: 700, fontSize: 10,
                      padding: '2px 8px', borderRadius: 10, textTransform: 'uppercase', letterSpacing: 0.3,
                    }}>{meta.label}</span>
                    {tags.slice(0, 3).map(t => (
                      <span key={t} style={{
                        fontSize: 10, color: 'var(--primary)', background: 'var(--primary-light)',
                        padding: '2px 7px', borderRadius: 10, fontWeight: 600,
                      }}>{t}</span>
                    ))}
                  </div>
                  <div style={{
                    fontWeight: 700, fontSize: 15, color: 'var(--gray-900)',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {p.title}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 2, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    <span>/blog/{p.slug}</span>
                    {p.published_at && <span>· {formatDate(p.published_at)}</span>}
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 6, flexShrink: 0, flexDirection: 'column', alignItems: 'flex-end' }}>
                  <button
                    className="btn btn-xs btn-secondary"
                    onClick={e => { e.stopPropagation(); navigate(`/master/blog/${p.id}/edit`); }}>
                    Editar
                  </button>
                  {p.status === 'published' && (
                    <a
                      className="btn btn-xs btn-secondary"
                      href={`/blog/${encodeURIComponent(p.slug)}`}
                      target="_blank" rel="noopener noreferrer"
                      onClick={e => e.stopPropagation()}>
                      Ver →
                    </a>
                  )}
                  <button
                    className="btn btn-xs btn-danger"
                    onClick={e => { e.stopPropagation(); handleDelete(p); }}>
                    Remover
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
